import type { FirebaseApp } from 'firebase/app';
import {
  collection,
  doc,
  limit as limiter,
  onSnapshot,
  orderBy,
  query,
  getDocs,
  where,
  type DocumentData,
  type Firestore,
  type Query,
  type QueryConstraint,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import type { EmulatorEndpoint } from '@taki/runtime-config';
import type { Connexion, Flux, FluxCle, FluxExecution, FluxStatutExecution, FluxVariable, OperationFlux } from '@taki/data-model';
import { cleDeBouton, fournisseurConnexion, sortiesExternes } from '@taki/data-model';
import type { ConnexionsClient } from './connexions.js';
import { definitionDepuisStockage } from '@taki/data-model';

/**
 * LES AUTOMATISATIONS (app Flux), COTE CLIENT : lire et demander.
 *
 * Le client LIT quatre collections, toutes sous `orgs/{orgId}/` (`flux`,
 * `fluxExecutions`, `fluxVariables`, et `fluxCles` pour l'owner et les admins), en temps reel, et il n'y ECRIT JAMAIS :
 * les rules refusent toute ecriture client. Tout geste (creer, modifier,
 * publier, activer, tester, approuver, creer une variable...) passe par UNE
 * callable a operations, `fluxApi`, qui valide par le schema du
 * socle (`packages/data-model/src/flux.ts`) et verifie les droits. Le moteur
 * vit dans `functions/src/flux/`, et nulle part ailleurs.
 *
 * DEUX REQUETES PAR LISTE, ET C'EST LES RULES QUI L'IMPOSENT. Une
 * automatisation se lit si elle est partagee a l'org (`portee == 'org'`) OU si
 * on en est l'auteur (`auteurUid == uid`). Firestore n'accepte une requete que
 * si chacun de ses resultats est permis par les rules : une requete sans
 * filtre serait refusee en bloc. D'ou deux requetes, chacune permise, puis une
 * fusion par id cote client (une automatisation org de l'auteur arrive par les
 * deux) et un tri cote client. `fluxExecutions` et `fluxVariables` recopient
 * `portee` et `auteurUid` pour la meme raison.
 *
 * LES SECRETS NE DESCENDENT JAMAIS. Une variable secrete a une `valeur` vide :
 * la valeur vit dans `fluxSecrets`, que les rules ferment a tout client.
 *
 * LES CONNEXIONS sont une brique du socle (`sdk.connexions`, `connexions.ts`) :
 * `observerConnexions` en rend celles qui signent une requete HTTP, sous la
 * forme du socle (`Connexion`).
 *
 * LES CONSTANTES CI-DESSOUS RECOPIENT `functions/src/flux/contrat.ts` et
 * `functions/src/runtime.ts` (`functions/` ne depend d'aucun `@taki/*`,
 * docs/FUNCTIONS.md).
 */

/** DOIT valoir `FUNCTIONS_REGION` de `functions/src/runtime.ts`. */
export const FLUX_FUNCTIONS_REGION = 'europe-west1';
/** `CALLABLE_FLUX` de `functions/src/flux/contrat.ts`. */
export const FLUX_CALLABLE = 'fluxApi';
export const FLUX_COLLECTION = 'flux';
export const FLUX_EXECUTIONS_COLLECTION = 'fluxExecutions';
export const FLUX_VARIABLES_COLLECTION = 'fluxVariables';
export const FLUX_CLES_COLLECTION = 'fluxCles';

/** Le nombre d'executions lues par defaut (les plus recentes). */
export const FLUX_EXECUTIONS_LIMITE_DEFAUT = 50;
/** Plafond dur d'une lecture d'executions. */
export const FLUX_EXECUTIONS_LIMITE_MAX = 200;

export interface FiltreListeFlux {
  /** `org` : Org wide ; `perso` : les siennes non partagees ; `toutes` (defaut) : tout ce qu'on voit. */
  portee?: 'org' | 'perso' | 'toutes';
  /** `true` : les archivees seulement ; defaut : les actives seulement. */
  archives?: boolean;
}

export interface FiltreExecutionsFlux {
  /** Une seule automatisation ; absent : toutes celles qu'on voit. */
  fluxId?: string;
  /** Les N plus recentes (defaut `FLUX_EXECUTIONS_LIMITE_DEFAUT`). */
  limite?: number;
}

/** Le clic d'un smart bouton `flux: true` (`docs/socle/smart-boutons.md`) : ce que le serveur relit lui-meme, le client ne dit que QUEL bouton et QUELLE fiche. */
export interface DeclencherBoutonEntree {
  appId: string;
  porteId: string;
  boutonId: string;
  /** Bouton de niveau fiche : la fiche du clic. */
  ficheId?: string;
  /** `true` : l'utilisateur a dit oui a la fenetre des envois hors de l'equipe (e-mail, http, connecteur). */
  confirme?: boolean;
  /**
   * `true` : le serveur fait tous les controles (declaration, droits, fiche, condition, flux branches) puis rend
   * `confirmation` avec les envois hors de l'equipe (peut-etre aucun), SANS rien lancer. C'est la fenetre unique
   * d'un bouton qui declare une confirmation : un seul ecran, puis le rappel avec `confirme: true`.
   */
  apercu?: boolean;
}

/** Un envoi hors de l'equipe qu'un flux branche s'apprete a faire, a montrer avant de relancer avec `confirme: true`. */
export interface SortieAConfirmer {
  fluxId: string;
  fluxNom: string;
  /** Lisible tel quel : « E-mail : Ecrire au client ». */
  libelle: string;
}

/** Un flux lance par le clic : son execution (`?execution=<id>` dans le suivi de Flux) et son verdict ; `executionId` vide si le flux n'a pas pu partir. */
export interface ExecutionDeBouton {
  fluxId: string;
  fluxNom: string;
  executionId: string;
  statut: FluxStatutExecution;
  erreur?: string;
}

/** Rien n'est parti (`confirmation`), ou les flux sont lances jusqu'a la fin ou la premiere pause (`lance`). */
export type DeclencherBoutonReponse =
  | { statut: 'confirmation'; sorties: SortieAConfirmer[] }
  | { statut: 'lance'; executions: ExecutionDeBouton[] };

/** Un flux actif branche sur un bouton : de quoi dire « 2 flux » et les nommer. */
export interface FluxDeBouton {
  fluxId: string;
  nom: string;
  /** Le flux publie envoie hors de l'equipe (`sortiesExternes`) : la confirmation du bouton est alors obligatoire. */
  sortExterne: boolean;
}

/**
 * Le contrat de `sdk.flux`, consomme par l'app Flux et son builder
 * (`apps/hello/src/apps/flux/builder/types.ts` en recopie la forme). Chaque
 * `observer*` rend sa fonction de desabonnement ; sans compte connecte, il
 * rend une liste vide (ou `null`) et ne lit rien.
 */
export interface FluxRepository {
  observerListe(filtre: FiltreListeFlux, cb: (flux: Flux[]) => void, erreur?: (e: unknown) => void): () => void;
  observer(fluxId: string, cb: (flux: Flux | null) => void, erreur?: (e: unknown) => void): () => void;
  observerExecutions(filtre: FiltreExecutionsFlux, cb: (executions: FluxExecution[]) => void, erreur?: (e: unknown) => void): () => void;
  observerExecution(id: string, cb: (execution: FluxExecution | null) => void, erreur?: (e: unknown) => void): () => void;
  observerConnexions(cb: (c: Connexion[]) => void, erreur?: (e: unknown) => void): () => void;
  observerVariables(cb: (v: FluxVariable[]) => void, erreur?: (e: unknown) => void): () => void;
  /**
   * Les CLES API des agents de l'org (`fluxCles`, leur fiche sans la valeur : nom,
   * apercu « ••••abcd », plafond, depense du jour). Les rules ne les donnent
   * qu'a l'owner et aux admins : ailleurs, la lecture est refusee (`erreur`).
   */
  observerCles(cb: (c: FluxCle[]) => void, erreur?: (e: unknown) => void): () => void;
  /**
   * Le clic d'un bouton `flux` : le serveur relit la declaration, la fiche et la
   * condition, puis lance les flux actifs branches. Leve une `FluxError`
   * (« Aucun flux actif sur ce bouton », « La condition du bouton n'est plus remplie »).
   * Une reponse `confirmation` n'a rien lance : la rappeler avec `confirme: true` apres le oui (`apercu: true` la rend toujours, sorties possiblement vides).
   */
  declencherBouton(entree: DeclencherBoutonEntree): Promise<DeclencherBoutonReponse>;
  /**
   * Les flux ACTIFS, publies, d'organisation, branches sur un bouton, par clef
   * `app/porte/bouton` (`cleDeBouton`), en UNE lecture. `appId` : seulement les
   * boutons de cette app. Une clef absente n'a aucun flux : le bouton est grise.
   */
  listerBranchementsDeBoutons(appId?: string): Promise<Record<string, FluxDeBouton[]>>;
  /** Une operation de `fluxApi`, de la liste `OPERATIONS_FLUX` (`creer`, `publier`, `activer`...). Leve une `FluxError` au message court. */
  appeler<T = unknown>(op: OperationFlux, donnees?: Record<string, unknown>): Promise<T>;
}

/**
 * LE NOM SOUS LEQUEL `TakiSdk` LE PORTE. Un alias, et c'est voulu : un
 * `xxxRepository` dans `TakiSdk` se lit comme une FAMILLE de fiches
 * (`scripts/check-app-entities.mjs`, `loadValidFamilies`) que l'app devrait
 * declarer dans `entities`. Les automatisations n'en sont pas une : c'est un
 * service transversal, comme `WhatsappClient` ou `JournalClient`.
 */
export type FluxClient = FluxRepository;

/** Une erreur de `fluxApi`, au message deja lisible (celui du serveur). */
export class FluxError extends Error {
  override readonly name = 'FluxError';
  readonly code: string;
  constructor(message: string, code: string, options?: { cause?: unknown }) {
    super(message);
    this.code = code;
    if (options?.cause !== undefined) this.cause = options.cause;
    Object.setPrototypeOf(this, FluxError.prototype);
  }
}

/* -------------------------------------------------------------------------- */
/* Fonctions pures (testees dans tests/flux.test.mjs)                          */
/* -------------------------------------------------------------------------- */

function estTimestamp(valeur: unknown): valeur is { toDate(): Date } {
  return valeur !== null && typeof valeur === 'object' && typeof (valeur as { toDate?: unknown }).toDate === 'function';
}

/**
 * LES DATES DE LA BASE DEVIENNENT DES `Date`, A TOUTE PROFONDEUR. La base range
 * des Timestamp (racine, `dernierRun.at`, `etapes[].debut`, `pause.reprendreA`) ;
 * le contrat (`Flux`, `FluxExecution`) promet des `Date`. Une conversion
 * recursive plutot qu'une liste de chemins : une liste oublierait le prochain
 * champ date, et l'ecran afficherait « [object Object] ».
 */
export function versDates<T = unknown>(valeur: unknown): T {
  if (estTimestamp(valeur)) return valeur.toDate() as T;
  if (valeur instanceof Date) return valeur as T;
  if (Array.isArray(valeur)) return valeur.map((v) => versDates(v)) as T;
  if (valeur !== null && typeof valeur === 'object') {
    const sortie: Record<string, unknown> = {};
    for (const [clef, v] of Object.entries(valeur as Record<string, unknown>)) sortie[clef] = versDates(v);
    return sortie as T;
  }
  return valeur as T;
}

/** Un document lu : ses donnees converties, son id pose (l'id du document gagne). */
export function lireDocumentFlux<T>(id: string, donnees: DocumentData): T {
  const lu: Record<string, unknown> = { ...versDates<Record<string, unknown>>(donnees), id };
  // Les definitions reviennent a la forme du contrat (Firestore range les
  // groupes de conditions en `{ et }`, `definitionDepuisStockage`).
  for (const clef of ['brouillon', 'publie'] as const) if (lu[clef] != null) lu[clef] = definitionDepuisStockage(lu[clef]);
  return lu as T;
}

/**
 * FUSIONNE LES RESULTATS DE PLUSIEURS REQUETES, par id, sans doublon (le
 * dernier lu gagne : c'est le meme document), puis trie. Les listes absentes
 * (requete pas encore revenue) comptent pour vides.
 */
export function fusionnerParId<T extends { id: string }>(listes: ReadonlyArray<readonly T[] | null>, comparer: (a: T, b: T) => number): T[] {
  const parId = new Map<string, T>();
  for (const liste of listes) for (const element of liste ?? []) parId.set(element.id, element);
  return [...parId.values()].sort(comparer);
}

/** Un document supprime (`deletedAt` pose) se lit comme introuvable. */
function estSupprime(donnees: DocumentData | undefined): boolean {
  return donnees?.['deletedAt'] != null;
}

function temps(date: unknown): number {
  return date instanceof Date ? date.getTime() : 0;
}

/** Les plus recemment modifiees d'abord. */
export const parModificationRecente = (a: { updatedAt?: unknown }, b: { updatedAt?: unknown }): number => temps(b.updatedAt) - temps(a.updatedAt);
/** Les plus recentes executions d'abord. */
export const parDebutRecent = (a: { debut?: unknown }, b: { debut?: unknown }): number => temps(b.debut) - temps(a.debut);

/**
 * LE TRI D'UNE LISTE D'AUTOMATISATIONS SELON SON FILTRE, sur la liste fusionnee.
 * `perso` garde les siennes NON partagees (une automatisation org de l'auteur
 * vit dans Org wide) ; les archives se tranchent ici, cote client, ce qui
 * epargne un index composite par combinaison. Une SUPPRIMEE (`deletedAt`) n'est
 * ni dans les listes ni dans les archives.
 */
export function filtrerListeFlux(flux: readonly Flux[], filtre: FiltreListeFlux, uid: string): Flux[] {
  const portee = filtre.portee ?? 'toutes';
  return flux.filter((f) => {
    if (f.deletedAt != null) return false;
    if ((f.archivedAt != null) !== (filtre.archives === true)) return false;
    if (portee === 'org') return f.portee === 'org';
    if (portee === 'perso') return f.portee === 'perso' && f.auteurUid === uid;
    return true;
  });
}

/**
 * LES BRANCHEMENTS DES BOUTONS, tires d'une liste de flux lisibles : actifs, publies,
 * d'organisation, ni archives ni supprimes, dont le declencheur PUBLIE est un bouton
 * plein. Rend `{ 'app/porte/bouton': [{ fluxId, nom, sortExterne }] }`, les flux d'un
 * bouton par nom (`sortExterne` : le flux publie envoie hors de l'equipe). `appId` borne aux boutons de cette app.
 */
export function branchementsDeBoutons(flux: readonly Flux[], appId?: string): Record<string, FluxDeBouton[]> {
  const parCle: Record<string, FluxDeBouton[]> = {};
  for (const f of flux) {
    const d = f.publie?.declencheur;
    if (d?.type !== 'bouton' || !f.actif || f.portee !== 'org' || f.archivedAt != null || f.deletedAt != null) continue;
    if (d.app === '' || d.porte === '' || d.bouton === '' || (appId !== undefined && d.app !== appId)) continue;
    (parCle[cleDeBouton(d.app, d.porte, d.bouton)] ??= []).push({
      fluxId: f.id,
      nom: f.nom,
      sortExterne: Array.isArray((f.publie as { etapes?: unknown } | null)?.etapes) && sortiesExternes(f.publie as Parameters<typeof sortiesExternes>[0]).length > 0,
    });
  }
  for (const liste of Object.values(parCle)) liste.sort((a, b) => a.nom.localeCompare(b.nom, 'fr') || a.fluxId.localeCompare(b.fluxId));
  return parCle;
}

/**
 * LES CONNEXIONS DU SOCLE QU'UNE ETAPE HTTP PEUT UTILISER : celles dont le
 * fournisseur rend le service `http`.
 */
export function connexionsPourHttp(connexions: readonly Connexion[]): Connexion[] {
  return connexions
    .filter((c) => fournisseurConnexion(c.fournisseur)?.services.includes('http') === true);
}

/** Le message d'une erreur de callable, sans l'habillage de Firebase. */
export function messageDErreurFlux(erreur: unknown): FluxError {
  const code = String((erreur as { code?: unknown } | null)?.code ?? 'inconnu').replace(/^functions\//, '');
  const brut = erreur instanceof Error ? erreur.message : String(erreur);
  const message = brut.replace(/^Firebase: /, '').replace(/ \(functions\/[a-z-]+\)\.?$/, '').trim();
  if (code === 'unavailable' || (code === 'internal' && message === 'internal')) {
    return new FluxError('Service des automatisations injoignable.', code, { cause: erreur });
  }
  if (code === 'unauthenticated') return new FluxError('Connexion requise.', code, { cause: erreur });
  return new FluxError(message === '' ? 'Échec.' : message, code, { cause: erreur });
}

/* -------------------------------------------------------------------------- */
/* Le client                                                                   */
/* -------------------------------------------------------------------------- */

const emulatorBoundFunctions = new WeakSet<Functions>();

/**
 * @param getActorId - uid de l'utilisateur connecte (`'system'` sans compte :
 *   rien n'est lu alors).
 * @param emulator - `config.emulators?.functions` en dev local, sinon omis.
 * @param connexions - `sdk.connexions`, a qui `observerConnexions` delegue.
 */
export function createFluxClient(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  app: FirebaseApp,
  emulator: EmulatorEndpoint | undefined,
  connexions: ConnexionsClient,
): FluxRepository {
  const col = (nom: string) => collection(db, 'orgs', orgId, nom);
  const uidConnecte = (): string | null => {
    const uid = getActorId();
    return uid === '' || uid === 'system' ? null : uid;
  };

  /**
   * Observe les deux requetes permises (org, auteur) et rend leur fusion des
   * que les DEUX ont repondu : une seule demi-liste serait un etat faux (une
   * automatisation perso « disparue » le temps d'un aller-retour).
   */
  function observerDeux<T extends { id: string }>(
    nom: string,
    contraintes: QueryConstraint[],
    rendre: (fusion: T[]) => void,
    comparer: (a: T, b: T) => number,
    erreur?: (e: unknown) => void,
  ): () => void {
    const uid = uidConnecte();
    if (uid === null) {
      rendre([]);
      return () => undefined;
    }
    const requetes: Query[] = [
      query(col(nom), where('portee', '==', 'org'), ...contraintes),
      query(col(nom), where('auteurUid', '==', uid), ...contraintes),
    ];
    const resultats: (T[] | null)[] = requetes.map(() => null);
    const arrets = requetes.map((q, i) =>
      onSnapshot(
        q,
        (snapshot) => {
          resultats[i] = snapshot.docs.map((d) => lireDocumentFlux<T>(d.id, d.data()));
          if (resultats.every((r) => r !== null)) rendre(fusionnerParId(resultats, comparer));
        },
        (e) => erreur?.(e),
      ),
    );
    return () => arrets.forEach((arreter) => arreter());
  }

  function observerUn<T>(nom: string, id: string, cb: (valeur: T | null) => void, erreur?: (e: unknown) => void): () => void {
    if (uidConnecte() === null || id === '') {
      cb(null);
      return () => undefined;
    }
    return onSnapshot(
      doc(db, 'orgs', orgId, nom, id),
      (snapshot) => cb(snapshot.exists() && !estSupprime(snapshot.data()) ? lireDocumentFlux<T>(snapshot.id, snapshot.data()) : null),
      (e) => {
        // Un document qu'on ne peut pas lire (perso d'un autre) se lit comme absent.
        if ((e as { code?: unknown }).code === 'permission-denied') cb(null);
        else erreur?.(e);
      },
    );
  }

  /** La callable, fabriquee au premier appel : un ecran qui ne fait que lire n'en a pas besoin. */
  let callable: ((donnees: Record<string, unknown>) => Promise<{ data: unknown }>) | null = null;
  function appelable(): (donnees: Record<string, unknown>) => Promise<{ data: unknown }> {
    if (callable !== null) return callable;
    const functions = getFunctions(app, FLUX_FUNCTIONS_REGION);
    if (emulator && !emulatorBoundFunctions.has(functions)) {
      connectFunctionsEmulator(functions, emulator.host, emulator.port);
      emulatorBoundFunctions.add(functions);
    }
    callable = httpsCallable<Record<string, unknown>, unknown>(functions, FLUX_CALLABLE);
    return callable;
  }

  async function appeler<T = unknown>(op: OperationFlux, donnees: Record<string, unknown> = {}): Promise<T> {
    try {
      const { data } = await appelable()({ ...donnees, op });
      return data as T;
    } catch (e) {
      throw messageDErreurFlux(e);
    }
  }

  return {
    observerListe(filtre, cb, erreur) {
      const uid = uidConnecte() ?? '';
      return observerDeux<Flux>(FLUX_COLLECTION, [], (fusion) => cb(filtrerListeFlux(fusion, filtre, uid)), parModificationRecente, erreur);
    },

    observer(fluxId, cb, erreur) {
      return observerUn<Flux>(FLUX_COLLECTION, fluxId, cb, erreur);
    },

    observerExecutions(filtre, cb, erreur) {
      const limite = Math.max(1, Math.min(FLUX_EXECUTIONS_LIMITE_MAX, Math.floor(filtre.limite ?? FLUX_EXECUTIONS_LIMITE_DEFAUT)));
      const contraintes: QueryConstraint[] = [
        ...(filtre.fluxId ? [where('fluxId', '==', filtre.fluxId)] : []),
        orderBy('debut', 'desc'),
        limiter(limite),
      ];
      // Chaque requete rend ses N plus recentes : la fusion en garde N.
      return observerDeux<FluxExecution>(FLUX_EXECUTIONS_COLLECTION, contraintes, (fusion) => cb(fusion.slice(0, limite)), parDebutRecent, erreur);
    },

    observerExecution(id, cb, erreur) {
      return observerUn<FluxExecution>(FLUX_EXECUTIONS_COLLECTION, id, cb, erreur);
    },

    observerConnexions(cb, erreur) {
      return connexions.observer((liste) => cb(connexionsPourHttp(liste)), erreur);
    },

    observerVariables(cb, erreur) {
      return observerDeux<FluxVariable>(FLUX_VARIABLES_COLLECTION, [], cb, parModificationRecente, erreur);
    },

    observerCles(cb, erreur) {
      const uid = uidConnecte();
      if (uid === null) {
        cb([]);
        return () => undefined;
      }
      return onSnapshot(
        col(FLUX_CLES_COLLECTION),
        (snapshot) => cb(snapshot.docs.map((d) => lireDocumentFlux<FluxCle>(d.id, d.data())).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))),
        (e) => erreur?.(e),
      );
    },

    async declencherBouton(entree) {
      return appeler<DeclencherBoutonReponse>('declencherBouton', { ...entree });
    },

    async listerBranchementsDeBoutons(appId) {
      if (uidConnecte() === null) return {};
      // Les flux de l'org seulement : la requete porte `portee == 'org'`, que les rules exigent (un perso n'a pas de bouton).
      const lus = await getDocs(query(col(FLUX_COLLECTION), where('portee', '==', 'org'), where('actif', '==', true), where('declencheurType', '==', 'bouton')));
      return branchementsDeBoutons(lus.docs.map((d) => lireDocumentFlux<Flux>(d.id, d.data())), appId);
    },

    appeler,
  };
}
